import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AppointmentsAPI from '../../api/appointments';
import { extractRecord, extractList } from '../utils/appointmentEnvelope';
import {
  createAccountContextGuard,
  createAccountRequestGuard,
  invalidateAccountRequestContext,
} from './utils/accountRequestGuard';
import { createAbortableRequestTracker } from './utils/abortableRequestTracker';
import { createOperationRequestTracker } from './utils/operationRequestTracker';

const getRequestGuard = createAccountRequestGuard();
const showRequestGuard = createAccountRequestGuard();
const metricsRequestGuard = createAccountRequestGuard();
const mutationContextGuard = createAccountContextGuard();
const readRequestTracker = createAbortableRequestTracker();
const mutationRequestTracker = createOperationRequestTracker();

const normalizeMemberPayload = payload =>
  payload && typeof payload === 'object' ? payload : { id: payload };

const beginMutationRequest = (
  localState,
  rootGetters,
  { accountId, generation, group, identity }
) => {
  const contextRequest = mutationContextGuard.begin(localState, {
    accountId: accountId ?? rootGetters.getCurrentAccountId,
    generation,
  });
  const isCurrentContext = () =>
    mutationContextGuard.isCurrent(localState, contextRequest, rootGetters);

  if (!isCurrentContext()) {
    return {
      shouldStart: false,
      isCurrent: () => false,
      finish: () => false,
    };
  }

  const operationRequest = mutationRequestTracker.begin(localState, {
    group,
    identity,
  });

  return {
    accountId: contextRequest.accountId,
    shouldStart: true,
    isCurrent: () =>
      isCurrentContext() &&
      mutationRequestTracker.isLatest(localState, operationRequest),
    finish: () => {
      const completion = mutationRequestTracker.finish(
        localState,
        operationRequest
      );
      return (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        isCurrentContext()
      );
    },
  };
};

const beginReadRequest = (
  localState,
  rootGetters,
  { accountId, generation, group, guard }
) => {
  const requestedAccountId = accountId ?? rootGetters.getCurrentAccountId;
  const initialContext = mutationContextGuard.begin(localState, {
    accountId: requestedAccountId,
    generation,
  });
  if (
    !mutationContextGuard.isCurrent(localState, initialContext, rootGetters)
  ) {
    return { shouldStart: false, isCurrent: () => false, finish: () => false };
  }

  const contextRequest = guard.begin(localState, {
    accountId: requestedAccountId,
    generation,
  });
  const isCurrentContext = () =>
    guard.isCurrent(localState, contextRequest, rootGetters);
  if (!isCurrentContext()) {
    return { shouldStart: false, isCurrent: () => false, finish: () => false };
  }

  const abortableRequest = readRequestTracker.begin(localState, group);
  return {
    accountId: contextRequest.accountId,
    shouldStart: true,
    signal: abortableRequest.signal,
    isCurrent: () =>
      isCurrentContext() &&
      readRequestTracker.isCurrent(localState, abortableRequest),
    finish: () => readRequestTracker.finish(localState, abortableRequest),
  };
};

export const state = {
  records: [],
  metrics: null,
  metricsLoading: false,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isCancelling: false,
    isExporting: false,
    isFetchError: false,
  },
};

export const getters = {
  getAppointments(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAppointmentById: _state => id => {
    return _state.records.find(record => record.id === Number(id)) || {};
  },
};

export const actions = {
  get: async function getAppointments(
    { commit, state: localState = state, rootGetters = {} },
    params = {}
  ) {
    const { accountId, generation, ...apiParams } = params;
    const request = beginReadRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'list',
      guard: getRequestGuard,
    });
    if (!request.shouldStart) return [];

    commit(types.SET_APPOINTMENT_UI_FLAG, {
      isFetching: true,
      isFetchError: false,
    });
    try {
      const response = await AppointmentsAPI.list(
        apiParams,
        request.accountId,
        { signal: request.signal }
      );
      const records = extractList(response);
      if (!request.isCurrent()) return [];

      commit(types.SET_APPOINTMENTS, records);
      return records;
    } catch (error) {
      if (request.isCurrent()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isFetchError: true });
        throw error;
      }
      return [];
    } finally {
      if (request.isCurrent()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isFetching: false });
      }
      request.finish();
    }
  },

  show: async function showAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload = {}
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginReadRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'show',
      guard: showRequestGuard,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AppointmentsAPI.show(id, request.accountId, {
        signal: request.signal,
      });
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.isCurrent()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isFetchingItem: false });
      }
      request.finish();
    }
  },

  // Audit 2026-05-07 — try/finally (sem catch) para preservar `error.response`
  // intacto até o caller. Antes reescrevia com `throw new Error(error.response.data.message)`,
  // mas `message` não existe no payload do backend (`{errors: {slot: [...]}}`),
  // virava `Error("undefined")`, e o modal caía no fallback GENERIC_ERROR
  // ("Base: Não foi possível criar..."). Bug reportado por Igor Silva.
  create: async function createAppointment(
    { commit, state: localState = state, rootGetters = {} },
    appointmentData = {}
  ) {
    const { accountId, generation, ...apiData } = appointmentData;
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isCreating',
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isCreating: true });
    try {
      const response = await AppointmentsAPI.create(apiData, request.accountId);
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.ADD_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isCreating: false });
      }
    }
  },

  update: async function updateAppointment(
    { commit, state: localState = state, rootGetters = {} },
    { id, accountId, generation, ...updateData }
  ) {
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.update(
        id,
        updateData,
        request.accountId
      );
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
      }
    }
  },

  cancel: async function cancelAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload = {}
  ) {
    const { id, reason, accountId, generation } =
      normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isCancelling',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isCancelling: true });
    try {
      // cancel → DELETE /appointments/:id → 204 No Content
      // Remove from local store; parent will re-fetch to get updated list
      await AppointmentsAPI.cancel(id, { reason }, request.accountId);
      if (!request.isCurrent()) return undefined;

      commit(types.DELETE_APPOINTMENT, id);
      return true;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isCancelling: false });
      }
    }
  },

  confirm: async function confirmAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.confirm(id, request.accountId);
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
      }
    }
  },

  complete: async function completeAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.complete(id, request.accountId);
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
      }
    }
  },

  noShow: async function noShowAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isUpdating',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.noShow(id, request.accountId);
      const record = extractRecord(response.data);
      if (!request.isCurrent()) return undefined;

      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
      }
    }
  },

  delete: async function deleteAppointment(
    { commit, state: localState = state, rootGetters = {} },
    payload
  ) {
    const { id, accountId, generation } = normalizeMemberPayload(payload);
    const request = beginMutationRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'isDeleting',
      identity: id,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENT_UI_FLAG, { isDeleting: true });
    try {
      await AppointmentsAPI.delete(id, request.accountId);
      if (!request.isCurrent()) return undefined;

      commit(types.DELETE_APPOINTMENT, id);
      return true;
    } catch (error) {
      if (request.isCurrent()) throw error;
      return undefined;
    } finally {
      if (request.finish()) {
        commit(types.SET_APPOINTMENT_UI_FLAG, { isDeleting: false });
      }
    }
  },

  fetchMetrics: async function fetchAppointmentsMetrics(
    { commit, state: localState = state, rootGetters = {} },
    { from, to, accountId, generation } = {}
  ) {
    const request = beginReadRequest(localState, rootGetters, {
      accountId,
      generation,
      group: 'metrics',
      guard: metricsRequestGuard,
    });
    if (!request.shouldStart) return undefined;

    commit(types.SET_APPOINTMENTS_METRICS_LOADING, true);
    try {
      const response = await AppointmentsAPI.metrics(
        { from, to },
        request.accountId,
        { signal: request.signal }
      );
      if (!request.isCurrent()) return undefined;

      commit(types.SET_APPOINTMENTS_METRICS, response.data.data);
      return response.data.data;
    } catch (_error) {
      // Ignore error — component shows empty state
      return undefined;
    } finally {
      if (request.isCurrent()) {
        commit(types.SET_APPOINTMENTS_METRICS_LOADING, false);
      }
      request.finish();
    }
  },
};

export const mutations = {
  RESET_ACCOUNT_DATA(_state) {
    invalidateAccountRequestContext(_state);
    readRequestTracker.reset(_state);
    mutationRequestTracker.reset(_state);
    _state.records = [];
    _state.metrics = null;
    _state.metricsLoading = false;
    _state.uiFlags = {
      isFetching: false,
      isFetchingItem: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isCancelling: false,
      isExporting: false,
      isFetchError: false,
    };
  },

  [types.SET_APPOINTMENT_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_APPOINTMENTS]: MutationHelpers.set,
  [types.ADD_APPOINTMENT]: MutationHelpers.create,
  [types.EDIT_APPOINTMENT]: MutationHelpers.update,
  [types.DELETE_APPOINTMENT]: MutationHelpers.destroy,

  [types.SET_APPOINTMENTS_METRICS](_state, data) {
    _state.metrics = data;
  },
  [types.SET_APPOINTMENTS_METRICS_LOADING](_state, loading) {
    _state.metricsLoading = loading;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
